import { useState, useMemo, useEffect } from 'react'
import { Plus, Search, Layers, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AircraftTable,
  AircraftMutateDrawer,
  DeleteAircraftDialog,
} from '@/components/aircrafts'
import { AircraftsFooter } from '@/components/aircrafts/AircraftsFooter'
import { AircraftDetailPanel } from '@/components/aircrafts/AircraftDetailPanel'
import { SavedViewTabs } from '@/components/aircrafts/SavedViewTabs'
import { QuickFilterPills } from '@/components/aircrafts/QuickFilterPills'
import { RecordStatusPill } from '@/components/data-table'
import { useDeleteAircraft, useRestoreAircraft } from '@/hooks/useAircraft'
import { useAircraftRegistry } from '@/hooks/useAircraftsRegistry'
import { useAircraftsRegistryStore } from '@/stores/aircraftsRegistryStore'
import { useAppContext } from '@/hooks/routing'
import {
  getSavedViewConfig,
  GROUPING_OPTIONS,
  AIRCRAFT_STATUS_LABELS,
  type SavedViewId,
  type GroupingOption,
  type AircraftTableRow,
  type AircraftRegistryRow,
} from '@/types/aircrafts'

/** Export aircraft data to CSV */
function exportAircraftCsv(data: AircraftTableRow[]) {
  const headers = [
    'Tail Number', 'Type', 'Manufacturer', 'Model',
    'Owner', 'Operator', 'Status', 'Presence', 'Relationship',
    'Home Location', 'Building', 'Unit', 'Arrived', 'ETD',
    'Insurance Expiry', 'Contract #', 'Monthly Rate', 'Lease End',
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = data.map((r: any) => [
    r.tail_number,
    r.aircraft_type_name || '',
    r.manufacturer || r.aircraft_type_manufacturer || '',
    r.model || r.aircraft_type_model || '',
    r.owner_client_name || r.owner_name || '',
    r.operator_client_name || '',
    AIRCRAFT_STATUS_LABELS[r.status as keyof typeof AIRCRAFT_STATUS_LABELS] || r.status || '',
    r.presence || '',
    r.relationship || '',
    r.home_location_name || '',
    r.building_name || '',
    r.unit_name || '',
    r.arrived_at || '',
    r.scheduled_departure || '',
    r.insurance_expiry || '',
    r.lease_contract_number || '',
    r.lease_monthly_rate?.toString() || '',
    r.lease_end_date || '',
  ])

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell: string) => `"${cell}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `aircraft_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
}

/**
 * Aircrafts Registry page (v3)
 *
 * Compact 2-row header:
 * - Row 1: Title + Saved View tabs + Add Aircraft button
 * - Row 2: Quick Filter Pills + Search + Grouping + Export
 */
export function AircraftsPage() {
  const { locationId, scope, locationName } = useAppContext()

  const {
    activeView,
    setActiveView,
    quickFilters,
    setPresenceFilter,
    setRelationshipFilter,
    setStageFilter,
    setLocationFilter,
    resetFiltersToViewDefaults,
    getIsModified,
    search,
    setSearch,
    groupBy,
    setGroupBy,
    recordStatus,
    setRecordStatus,
  } = useAircraftsRegistryStore()

  // Auto-switch to 'all' view when at org scope and current view requires location
  useEffect(() => {
    if (scope === 'org') {
      const config = getSavedViewConfig(activeView)
      if (config.requiresLocation) {
        setActiveView('all')
      }
    }
  }, [scope, activeView, setActiveView])

  // Fetch unified data
  const { data, allData, isLoading, summary, viewCounts } = useAircraftRegistry(
    activeView,
    quickFilters,
    locationId,
    recordStatus
  )

  const isModified = getIsModified()

  // Extract unique location names from all data for the Location filter pill
  const locationOptions = useMemo(() => {
    const names = new Set<string>()
    for (const row of allData) {
      if (row.home_location_name) names.add(row.home_location_name)
    }
    return Array.from(names).sort()
  }, [allData])

  // Determine which views should be disabled
  const disabledViews = useMemo((): SavedViewId[] => {
    if (scope === 'org') {
      return ['ramp', 'transients']
    }
    return []
  }, [scope])

  // Sheet/Dialog state
  const [sheetState, setSheetState] = useState<{
    open: boolean
    aircraftId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    tailNumber: string
  } | null>(null)

  // Detail panel state
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(null)
  const selectedAircraft = useMemo(
    () => (selectedAircraftId ? (data.find((a) => a.id === selectedAircraftId) as AircraftRegistryRow | undefined) ?? null : null),
    [selectedAircraftId, data]
  )

  const deleteAircraft = useDeleteAircraft()
  const restoreAircraftMutation = useRestoreAircraft()

  const handleCreate = () => {
    setSheetState({ open: true, aircraftId: undefined })
  }

  const handleEdit = (id: string) => {
    setSheetState({ open: true, aircraftId: id })
  }

  const handleDelete = (id: string, tailNumber: string) => {
    setDeleteTarget({ id, tailNumber })
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetState((prev) => ({
      open,
      aircraftId: open ? prev.aircraftId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteAircraft.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  const handleRowClick = (id: string) => {
    setSelectedAircraftId(id)
  }

  const subtitle =
    scope === 'location' && locationName
      ? `at ${locationName}`
      : undefined

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Row 1: Title + Saved View Tabs + Add Aircraft */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-baseline gap-2 shrink-0">
          <h1 className="text-2xl font-bold tracking-tight">Aircrafts</h1>
          {subtitle && (
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {subtitle}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <SavedViewTabs
            activeView={activeView}
            onViewChange={setActiveView}
            counts={viewCounts}
            isModified={isModified}
            disabledViews={disabledViews}
          />
        </div>

        <Button onClick={handleCreate} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Aircraft
        </Button>
      </div>

      {/* Row 2: Search + Quick Filter Pills + Toolbar */}
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tail#, type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        <div className="h-4 w-px bg-border mx-1" />

        <QuickFilterPills
          quickFilters={quickFilters}
          onPresenceChange={setPresenceFilter}
          onRelationshipChange={setRelationshipFilter}
          onStageChange={setStageFilter}
          onLocationChange={setLocationFilter}
          locationOptions={locationOptions}
          isModified={isModified}
          onReset={resetFiltersToViewDefaults}
        />

        <RecordStatusPill value={recordStatus} onChange={setRecordStatus} />

        <div className="flex items-center gap-2 ml-auto">
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupingOption)}>
            <SelectTrigger className="h-8 w-[140px]">
              <Layers className="mr-2 h-3.5 w-3.5" />
              <SelectValue placeholder="Group by..." />
            </SelectTrigger>
            <SelectContent>
              {GROUPING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => exportAircraftCsv(data)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Location Required Notice */}
      {scope === 'org' && (activeView === 'ramp' || activeView === 'transients') && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30 p-3 flex-shrink-0">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Select a location from the sidebar to view on-field aircraft.
          </p>
        </div>
      )}

      {/* Aircraft Table */}
      <div className="flex-1 min-h-0">
        <AircraftTable
          data={data}
          isLoading={isLoading}
          activeView={activeView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRestore={(id) => restoreAircraftMutation.mutate(id)}
          onRowClick={handleRowClick}
        />
      </div>

      {/* Footer Summary Bar */}
      <AircraftsFooter summary={summary} activeView={activeView} />

      {/* Aircraft Detail Panel */}
      <AircraftDetailPanel
        aircraft={selectedAircraft}
        open={!!selectedAircraftId}
        onOpenChange={(open) => {
          if (!open) setSelectedAircraftId(null)
        }}
        onEdit={handleEdit}
      />

      {/* Create/Edit Aircraft Drawer */}
      <AircraftMutateDrawer
        open={sheetState.open}
        onOpenChange={handleSheetOpenChange}
        aircraftId={sheetState.aircraftId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteAircraftDialog
        open={!!deleteTarget}
        onOpenChange={handleCloseDeleteDialog}
        tailNumber={deleteTarget?.tailNumber ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteAircraft.isPending}
      />
    </div>
  )
}
